import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RiskScoreResult from '../../components/RiskScoreResult';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import YesNoToggle from '../../components/YesNoToggle';
import { etRiskGroups } from '../../data/diseasesData';
import { RiskLevel } from '../../types';
import { useUserInput } from '../../context/UserInputContext';

const IpsetCalculator: React.FC = () => {
  const { t } = useTranslation('hematology');
  const { state, update } = useUserInput();
  const input = state.etRisk;
  const setInput = (val: typeof input) => update('etRisk', val);

  const activeLevel = useMemo<RiskLevel | null>(() => {
    const age = parseFloat(input.age);
    if (
      isNaN(age) ||
      input.jak2Mutation === null ||
      input.priorThrombosis === null
    ) {
      return null;
    }

    // High: thrombosis at any age, or age >60 with JAK2
    if (input.priorThrombosis || (age > 60 && input.jak2Mutation)) return 'high';
    // Intermediate: age >60, no JAK2, no thrombosis
    if (age > 60) return 'intermediate';
    // Low: age ≤60, JAK2+, no thrombosis
    if (input.jak2Mutation) return 'low';
    // Very low: age ≤60, no JAK2, no thrombosis
    return 'very-low';
  }, [input]);

  const inputs = (
    <>
      <NumberField
        label={t('et.ageLabel')}
        value={input.age}
        onChange={(v) => setInput({ ...input, age: v })}
        placeholder={t('et.agePlaceholder')}
        min={0}
        max={120}
      />

      <YesNoToggle
        label={t('et.jak2Label')}
        value={input.jak2Mutation}
        onChange={(v) => setInput({ ...input, jak2Mutation: v })}
      />

      <YesNoToggle
        label={t('et.thrombosisLabel')}
        value={input.priorThrombosis}
        onChange={(v) => setInput({ ...input, priorThrombosis: v })}
      />
    </>
  );

  return (
    <CalcShell
      title="Revised IPSET-Thrombosis"
      subtitle={t('et.riskSubtitle')}
      inputs={inputs}
    >
      <RiskScoreResult
        groups={etRiskGroups}
        activeLevel={activeLevel}
        emptyHint={t('et.emptyHint')}
      />
    </CalcShell>
  );
};

export default IpsetCalculator;
